import { useMemo, useState } from 'react'
import { Alert, Badge, Button, Code, Group, Paper, Select, Stack, Text, Title } from '@mantine/core'
import { can } from '@racedash/shared'
import type { Role } from '@racedash/shared'
import { useShareLinks } from './useShareLinks'
import { mintShareLink, revokeShareLink, shareUrl, MAX_LINKS_PER_DASHBOARD } from './api'
import { relativeTime } from '../lib/time'
import { currentEvent, useAttachedEvents } from '../dashboard/useDashboardSource'

export default function SharePanel({
  dashboardId,
  orgId,
  uid,
  role,
  hasActiveLink,
  isPrivate,
}: {
  dashboardId: string
  orgId: string
  uid: string
  role: Role | null
  hasActiveLink: boolean
  isPrivate: boolean
}) {
  const mayManage = can(role, 'shareLink.create')
  const { links, error } = useShareLinks(dashboardId, orgId, mayManage)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)

  /**
   * A link is minted for one event.
   *
   * Which means one has to be chosen, and defaulting to the one happening now
   * makes that a confirmation rather than a decision — "share this weekend" is
   * what people are actually doing when they reach for this button.
   */
  const { events } = useAttachedEvents(orgId)
  const [eventId, setEventId] = useState<string | null>(null)
  const chosen = useMemo(() => {
    const list = events ?? []
    const preferred = eventId ? list.find((e) => e.eventId === eventId) : null
    return preferred ?? currentEvent(list, new Date())
  }, [events, eventId])

  // Viewers can see THAT it is shared, but never the token. The badge comes
  // from the dashboard document, which the share trigger maintains.
  if (!mayManage) {
    return (
      <Paper withBorder p="lg">
        <Group gap="xs" mb={4}>
          <Title order={2} size="h5">
            Sharing
          </Title>
          {hasActiveLink && (
            <Badge variant="light" color="green" size="sm">
              live link
            </Badge>
          )}
        </Group>
        <Text c="dimmed" size="sm">
          {hasActiveLink
            ? 'This dashboard has a live public link. Anyone holding it can view the race view.'
            : 'This dashboard is not shared publicly.'}
        </Text>
      </Paper>
    )
  }

  const atLimit = (links?.length ?? 0) >= MAX_LINKS_PER_DASHBOARD
  // The ladder: a link is an add-on to organisation visibility, so it cannot
  // exist while the dashboard is hidden from the organisation itself.
  const blocked = isPrivate

  async function copy(token: string) {
    await navigator.clipboard.writeText(shareUrl(token))
    setCopied(token)
    setTimeout(() => setCopied(null), 2000)
  }

  return (
    <Paper withBorder p="lg">
      <Group justify="space-between" align="flex-start" mb="xs">
        <Title order={2} size="h5">
          Sharing
        </Title>
        <Group gap="xs" wrap="nowrap" align="flex-start">
          <Select
            size="xs"
            w={230}
            aria-label="Event this link is for"
            placeholder={events?.length ? 'Event' : 'Nothing entered yet'}
            data={(events ?? []).map((e) => ({
              value: e.eventId,
              label: e.startsAt ? `${e.name} — ${e.startsAt.toLocaleDateString()}` : e.name,
            }))}
            value={chosen?.eventId ?? null}
            onChange={setEventId}
            disabled={!events?.length}
            searchable
          />
          <Button
            size="compact-sm"
            variant="default"
            loading={busy}
            disabled={atLimit || blocked || !chosen}
            title={blocked ? 'Share it with your organisation first' : undefined}
            onClick={() => {
              if (!chosen) return
              setBusy(true)
              void mintShareLink(dashboardId, orgId, uid, {
                id: chosen.eventId,
                endsAt: chosen.endsAt,
              }).finally(() => setBusy(false))
            }}
          >
            Create link
          </Button>
        </Group>
      </Group>

      <Text c="dimmed" size="sm" mb="md">
        A link is for one event, and stops working a week after it ends — a link forwarded to a
        group chat should not still open your timing next season. It is unlisted rather than
        secret: anyone holding it can open the race view and it may be forwarded, but it can never
        reach anything private to your organisation. One link serves any number of people; create
        more only when you want to revoke audiences separately.
      </Text>

      <Stack gap="sm">
        {blocked && (
          <Alert color="yellow">
            This dashboard is set to <strong>Only me</strong>, so it can't have a public link. Share
            it with your organisation first — you can't show something to strangers that your own
            colleagues can't see.
          </Alert>
        )}

        {error && <Alert color="red">{error}</Alert>}

        {links?.length === 0 && (
          <Text c="dimmed" size="sm">
            No links yet.
          </Text>
        )}

        {links?.map((link) => (
          <Paper withBorder p="sm" key={link.token}>
            <Group justify="space-between" wrap="nowrap" align="flex-start">
              <div style={{ minWidth: 0 }}>
                <Code block style={{ fontSize: 11, overflowWrap: 'anywhere' }}>
                  {shareUrl(link.token)}
                </Code>
                <Text size="xs" c="dimmed" mt={4}>
                  created {relativeTime(link.createdAt)}
                </Text>
              </div>
              <Group gap={4} wrap="nowrap">
                <Button variant="subtle" size="compact-sm" onClick={() => void copy(link.token)}>
                  {copied === link.token ? 'Copied' : 'Copy'}
                </Button>
                <Button
                  variant="subtle"
                  color="red"
                  size="compact-sm"
                  onClick={() => void revokeShareLink(link.token)}
                >
                  Revoke
                </Button>
              </Group>
            </Group>
          </Paper>
        ))}

        {atLimit && (
          <Text c="dimmed" size="xs">
            Limit of {MAX_LINKS_PER_DASHBOARD} links reached. Revoke one to create another.
          </Text>
        )}
      </Stack>
    </Paper>
  )
}
